/**
 * 通用粒子池(方案 G3)。所有高频粒子走对象池 + ParticleContainer,免 GC。
 * 按 (texture, blend) 惰性分子池;每子池预分配一批 Particle,回收复用。
 */
import { Container, ParticleContainer, Particle, Texture } from 'pixi.js';

export interface SpawnCfg {
  x: number; y: number;
  vx?: number; vy?: number; ax?: number; ay?: number;
  life: number;                 // 秒
  scale?: number; scaleX?: number; scaleY?: number;
  tint?: number; alpha?: number;
  blend?: 'normal' | 'add';
  texture: Texture;
  rotation?: number; spin?: number;      // rad, rad/s
  fade?: 'quad' | 'linear' | 'none';     // 默认 quad:(1-t)^2
  onUpdate?: (self: Entry, dt: number) => void;
}

export class Entry {
  p!: Particle;
  vx = 0; vy = 0; ax = 0; ay = 0;
  life = 1; age = 0; baseAlpha = 1; sx = 1; sy = 1; spin = 0;
  fade: 'quad' | 'linear' | 'none' = 'quad';
  onUpdate?: (self: Entry, dt: number) => void;
  active = false;
  hit = false;
  get x() { return this.p.x; } set x(v: number) { this.p.x = v; }
  get y() { return this.p.y; } set y(v: number) { this.p.y = v; }
}

interface Sub { pc: ParticleContainer; entries: Entry[]; free: number[]; }

// 全局预算(Stage 按平台设置)
export const budget = { max: 2500, active: 0 };

export class ParticlePool {
  private subs = new Map<string, Sub>();
  constructor(private layer: Container, private chunk = 700) {}

  private sub(tex: Texture, blend: 'normal' | 'add'): Sub {
    const key = `${(tex as any).uid ?? 'tex'}_${blend}`;
    let s = this.subs.get(key);
    if (!s) {
      // 属性键必须用 pixi v8 particleData 的真名:vertex/position/rotation/uvs/color。
      // 没有叫 "scale" 的属性——缩放折叠在 vertex 里(vertex = 纹理尺寸×scale×anchor)。
      // 旧写法 `scale: true` 是无效键被静默忽略,vertex 保持 static:静态属性只在
      // addParticle 置 dirty 时上传一次,而本池在构造时就预分配了全部粒子(scale=1),
      // 之后 spawn 只改 JS 字段、从不触发重传 —— 结果所有粒子永远按纹理原始尺寸
      // 渲染(core 128px/glow 256px 的白色巨球)。这是"画面中央大光斑"和一切粒子
      // 效果全糊的总根因。color 同理必须 dynamic,否则 tint/alpha 渐隐全部失效。
      const pc = new ParticleContainer({
        dynamicProperties: { vertex: true, position: true, rotation: true, color: true },
      });
      pc.blendMode = blend;
      this.layer.addChild(pc);
      s = { pc, entries: [], free: [] };
      for (let i = 0; i < this.chunk; i++) {
        const part = new Particle({ texture: tex, x: -9999, y: -9999, anchorX: 0.5, anchorY: 0.5, alpha: 0 });
        pc.addParticle(part);
        const e = new Entry(); e.p = part;
        s.entries.push(e); s.free.push(i);
      }
      this.subs.set(key, s);
    }
    return s;
  }

  spawn(cfg: SpawnCfg): Entry | null {
    if (budget.active >= budget.max) return null;
    const blend = cfg.blend ?? 'normal';
    const s = this.sub(cfg.texture, blend);
    const idx = s.free.pop();
    if (idx == null) return null;
    const e = s.entries[idx];
    const p = e.p;
    p.x = cfg.x; p.y = cfg.y;
    e.vx = cfg.vx ?? 0; e.vy = cfg.vy ?? 0; e.ax = cfg.ax ?? 0; e.ay = cfg.ay ?? 0;
    e.life = cfg.life; e.age = 0;
    e.baseAlpha = cfg.alpha ?? 1;
    e.sx = cfg.scaleX ?? cfg.scale ?? 1;
    e.sy = cfg.scaleY ?? cfg.scale ?? 1;
    e.spin = cfg.spin ?? 0;
    e.fade = cfg.fade ?? 'quad';
    e.onUpdate = cfg.onUpdate;
    e.active = true; e.hit = false;
    p.scaleX = e.sx; p.scaleY = e.sy;
    p.rotation = cfg.rotation ?? 0;
    p.tint = cfg.tint ?? 0xffffff;
    p.alpha = e.baseAlpha;
    budget.active++;
    // 记录归还所需的 sub/idx
    (e as any)._sub = s; (e as any)._idx = idx;
    return e;
  }

  update(dt: number) {
    for (const s of this.subs.values()) {
      for (const e of s.entries) {
        if (!e.active) continue;
        e.age += dt;
        e.vx += e.ax * dt; e.vy += e.ay * dt;
        e.p.x += e.vx * dt; e.p.y += e.vy * dt;
        if (e.spin) e.p.rotation += e.spin * dt;
        const t = e.age / e.life;
        if (e.fade === 'quad') e.p.alpha = e.baseAlpha * (1 - t) * (1 - t);
        else if (e.fade === 'linear') e.p.alpha = e.baseAlpha * (1 - t);
        if (e.onUpdate) e.onUpdate(e, dt);
        if (e.age >= e.life || e.p.alpha <= 0.002) this.recycle(e);
      }
    }
  }

  private recycle(e: Entry) {
    e.active = false;
    e.p.alpha = 0; e.p.x = -9999; e.p.y = -9999;
    const s = (e as any)._sub as Sub, idx = (e as any)._idx as number;
    s.free.push(idx);
    budget.active = Math.max(0, budget.active - 1);
  }
}

// 由 Stage 装配后供各效果 import 使用(ESM 活绑定)
export let eventPool: ParticlePool;
export let weatherPool: ParticlePool;
export function setPools(event: ParticlePool, weather: ParticlePool) {
  eventPool = event; weatherPool = weather;
}
